import { GetStaticPropsContext, InferGetStaticPropsType } from "next";

import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import { usePageTracking } from "~/contexts/page-trackings";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import helpCentreUtils from "~/utils/help-centre-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetStaticPropsType<typeof getStaticProps>;

export default function CustomerHelpCenter(props: Props) {
  usePageTracking("editorial", { ...TrackingUtils.mapTrackEditorialPage(props), title: props.faq.page.title });

  return (
    <LayoutPage
      {...props}
      hreflangs={props.hreflangs}
      seoTitle={props.faq.page.pageType === "index" ? undefined : props.faq.page.title}
    >
      <>
        {props.page.components?.filter(isActiveEntry).map((entry) => (
          <EditorialEntry key={entry.sys.id} entry={entry} />
        ))}
      </>
    </LayoutPage>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const notFound = { notFound: true as const, revalidate: Constants.DEFAULT_STATIC_PAGE_TIME };

  const [sitemap, pageData] = await Promise.all([
    helpCentreUtils.getSitemap(context.locale!),
    ssrUtils.getServerSideEditorialPageData(context, context.draftMode, [helpCentreUtils.editorialSlug]),
  ]);

  const faqPage = sitemap && helpCentreUtils.findPage(sitemap, context.locale!, context.params?.slug as string[]);

  if (!sitemap || !faqPage || !pageData.page) {
    return notFound;
  }

  const html = await helpCentreUtils.getMarkup(faqPage, context.locale!);

  if (!html) {
    return notFound;
  }

  return {
    props: {
      ...pageData,
      faq: { html, page: faqPage, assets: helpCentreUtils.getAssets(html, sitemap.mainCssHref) },
      hreflangs: helpCentreUtils.getHreflangs(sitemap, faqPage),
    },
    revalidate: Constants.HELP_CENTRE_STATIC_PAGE_TIME,
  };
}
